import React from 'react';
import { motion } from 'framer-motion';
import { CalendarCheck, MapPinned, Car, BookOpen } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const HowItWorks = () => {
  const { t } = useLanguage();

  const steps = [
    {
      icon: CalendarCheck,
      title: t.howItWorks.step1.title,
      description: t.howItWorks.step1.desc,
      number: '01'
    },
    {
      icon: MapPinned,
      title: t.howItWorks.step2.title,
      description: t.howItWorks.step2.desc,
      number: '02'
    },
    {
      icon: Car,
      title: t.howItWorks.step3.title,
      description: t.howItWorks.step3.desc,
      number: '03'
    },
    {
      icon: BookOpen,
      title: t.howItWorks.step4.title,
      description: t.howItWorks.step4.desc,
      number: '04'
    }
  ];

  return (
    <section 
      className="py-24 md:py-32 bg-white"
      data-testid="how-it-works-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <p className="text-xs uppercase tracking-[0.3em] text-[#C2410C] mb-4 font-semibold">
            Proceso
          </p>
          <h2 className="font-display text-4xl sm:text-5xl font-bold text-[#1C1917]">
            {t.howItWorks.title}
          </h2>
        </motion.div>

        {/* Steps Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {steps.map((step, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.15 }}
              className="relative group"
              data-testid={`how-it-works-step-${index + 1}`}
            >
              {/* Connector line (hidden on mobile) */}
              {index < steps.length - 1 && (
                <div className="hidden lg:block absolute top-12 left-[60%] w-full h-[2px] bg-[#E7E5E4]">
                  <div className="absolute right-0 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-[#C2410C]" />
                </div>
              )}

              <div className="bg-[#FDFBF7] rounded-2xl p-8 text-center transition-all duration-300 hover:shadow-xl">
                {/* Number */}
                <span className="absolute top-4 right-4 font-display text-5xl font-bold text-[#C2410C]/10">
                  {step.number}
                </span>

                {/* Icon */}
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#C2410C] flex items-center justify-center text-white group-hover:scale-110 transition-transform duration-300">
                  <step.icon className="h-8 w-8" />
                </div>

                {/* Title */}
                <h3 className="font-display text-xl font-bold text-[#1C1917] mb-3">
                  {step.title}
                </h3>

                {/* Description */}
                <p className="text-[#57534E] text-sm leading-relaxed">
                  {step.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
